import type { ComponentType } from "react";
import type { BaseFrontmatter, PaperFrontmatter } from "@/content/schema";

export interface MdxModule<T extends BaseFrontmatter = BaseFrontmatter> {
  default: ComponentType;
  frontmatter: T;
}

export interface ContentRecord<T extends BaseFrontmatter = BaseFrontmatter> {
  Component: ComponentType;
  meta: T;
}

function published<T extends BaseFrontmatter>(
  modules: Record<string, MdxModule<T>>,
): ContentRecord<T>[] {
  return Object.values(modules)
    .filter((mod) => mod.frontmatter?.status === "published")
    .map((mod) => ({ Component: mod.default, meta: mod.frontmatter }))
    .sort((a, b) =>
      (b.meta.originalPublishedAt ?? b.meta.sitePublishedAt).localeCompare(
        a.meta.originalPublishedAt ?? a.meta.sitePublishedAt,
      ),
    );
}

const workModules = import.meta.glob<MdxModule>(
  "../../../content/work/*.mdx",
  { eager: true },
);
const thinkingModules = import.meta.glob<MdxModule>(
  "../../../content/thinking/*.mdx",
  { eager: true },
);
const publicationModules = import.meta.glob<MdxModule>(
  "../../../content/publications/*.mdx",
  { eager: true },
);
const paperModules = import.meta.glob<MdxModule<PaperFrontmatter>>(
  "../../../content/papers/*.mdx",
  { eager: true },
);
const gptModules = import.meta.glob<MdxModule>(
  "../../../content/gpts/*.mdx",
  { eager: true },
);
const nowModules = import.meta.glob<MdxModule>(
  "../../../content/now/*.mdx",
  { eager: true },
);

export const work = published(workModules);
export const thinking = published(thinkingModules);
export const publications = published(publicationModules);
export const papers = published(paperModules);
export const gpts = published(gptModules);
export const now = published(nowModules);

export function bySlug<T extends BaseFrontmatter>(
  records: ContentRecord<T>[],
  slug: string,
): ContentRecord<T> | undefined {
  return records.find((record) => record.meta.slug === slug);
}

export function featured<T extends BaseFrontmatter>(
  records: ContentRecord<T>[],
): ContentRecord<T>[] {
  return records.filter((record) => record.meta.featured);
}
